import { SyntheticEvent, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { DevTool } from "@hookform/devtools";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  paperClasses,
  Portal,
  Stack,
  Typography as T,
} from "@mui/material";
import Accordion, {
  AccordionSummary,
  AccordionDetails,
} from "datasets/pages/tables/components/Accordion";
import ImportDetails from "datasets/pages/tables/components/ImportDetails";
import ReviewDetails from "datasets/pages/tables/components/ReviewDetails";
import TableMetadataDetails from "datasets/pages/tables/components/TableMetadataDetails";
import { useIngestionHydration } from "datasets/pages/tables/components/file-validation/hooks/useIngestionHydration";
import useIngestContext from "datasets/pages/tables/contexts/ingest";
import useActiveIngests from "datasets/pages/tables/hooks/useActiveIngests";
import { Ingest } from "datasets/pages/tables/types/interfaces/ingest";
import { normalizeColumnNames } from "datasets/pages/tables/utils/columns";
import yn from "yn";
import useErrorToast from "~/contexts/error-toast";
import rumClient, { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API, VITE_DATASET_INGESTION_JOBS_ENABLED } = import.meta.env;

const IngestForm = () => {
  const navigate = useNavigate();
  const { datasetId } = useParams();
  const { showError } = useErrorToast();
  const { mutate: mutateActiveIngests } = useActiveIngests();
  const { setErrors, setDataProfile, setIsFileConfirmed, tableExists } = useIngestContext();
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Hydration: repopulate form from validated ingestion on page refresh
  const { hydratedData } = useIngestionHydration();
  const [searchParams] = useSearchParams();
  const currentIngestionId = searchParams.get("ingestion");

  const form = useForm<Ingest>({
    mode: "onChange",
    defaultValues: {
      file_size: 0,
      platform_storage_key: "",
      metadata_platform_storage_key: "",
      metadata_config: {
        columns: [],
        database_schema: "",
        table_description: "",
        table: "",
        tags: [],
      },
    },
  });

  // Reset form when switching to a different ingestion (e.g. "Start New Validation")
  const prevIngestionIdRef = useRef<string | null>(currentIngestionId);
  useEffect(() => {
    if (
      prevIngestionIdRef.current !== null &&
      currentIngestionId !== null &&
      prevIngestionIdRef.current !== currentIngestionId
    ) {
      form.reset();
      setDataProfile(null);
      setErrors([]);
      setIsFileConfirmed(false);
      hasHydrated.current = false;
    }
    prevIngestionIdRef.current = currentIngestionId;
  }, [currentIngestionId, form, setDataProfile, setErrors, setIsFileConfirmed]);

  // Populate form from hydrated validation data (runs once after hydration completes)
  const hasHydrated = useRef(false);
  useEffect(() => {
    if (hydratedData && !hasHydrated.current) {
      hasHydrated.current = true;

      if (hydratedData.sourceFileUri) {
        form.setValue("source_file_uri", hydratedData.sourceFileUri);
      }
      if (hydratedData.platformStorageKey) {
        form.setValue("platform_storage_key", hydratedData.platformStorageKey);
      }
      if (hydratedData.fileFormat) {
        form.setValue("metadata_config.file_format", hydratedData.fileFormat);
      }
      if (hydratedData.dataProfile?.columns) {
        const profileColumns = hydratedData.dataProfile.columns.map((col) => ({
          name: col.name,
          type: col.type,
          description: "",
          tags: [],
        }));
        form.setValue("metadata_config.columns", profileColumns);
      }
      if (hydratedData.dataProfile) {
        setDataProfile(hydratedData.dataProfile);
      }

      setIsFileConfirmed(true);
    }
  }, [hydratedData, form, setDataProfile, setIsFileConfirmed]);

  const [expanded, setExpanded] = useState<string | false>("panel1");
  const storageKey = form.watch("platform_storage_key");
  const destination = form.watch("metadata_config.database_schema");
  const tableName = form.watch("metadata_config.table");
  const columns = form.watch("metadata_config.columns");

  // Check if we have valid columns for ingestion
  const isFormValidForIngestion = form.formState.isValid && columns && columns.length > 0;

  const handleExpand = (panel: string) => (_e: SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
  };

  const doSubmit = async (data: Ingest) => {
    setErrors([]);

    //We only want to send one of source_file_uri or platform_storage_key
    //Prefer source_file_uri if it's present
    try {
      const body = {
        dataset_id: datasetId,
        ...(data.source_file_uri
          ? { source_file_uri: data.source_file_uri }
          : { platform_storage_key: data.platform_storage_key }),
        metadata_config: {
          ...data.metadata_config,
          columns: normalizeColumnNames(data.metadata_config.columns),
        },
      };

      const resp = await fetch(`${VITE_CATALOG_API}/ingests`, {
        method: "POST",
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json" },
      });

      if (!resp.ok) {
        const errorResp = await resp.json();
        const errorBody = JSON.parse(errorResp).details;
        const details = Array.isArray(errorBody) ? errorBody : [errorBody];

        return setErrors(details);
      }

      trackEvent("dataset_table_ingest_submitted", {
        dataset_id: datasetId,
        table: data.metadata_config.table,
        database_schema: data.metadata_config.database_schema,
        columns: data.metadata_config.columns.length,
      });

      mutateActiveIngests();
      navigate(`/data-browser/datasets/${datasetId}/tables`);
    } catch (e: any) {
      console.error(e);
      rumClient?.recordError(e);
      showError("There was a problem attempting to ingest the data.");
    }
  };

  const onSubmit = (data: Ingest) => {
    if (tableExists) {
      setConfirmOpen(true);
    } else {
      return doSubmit(data);
    }
  };

  return (
    <>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Stack gap={1}>
            {/* 1. Data Import */}
            <Accordion expanded={expanded === "panel1"} onChange={handleExpand("panel1")}>
              <AccordionSummary>
                <T variant="body2" fontWeight={500}>
                  1. Data Import
                </T>
              </AccordionSummary>
              <AccordionDetails
                sx={{ maxWidth: 690, [`.${paperClasses.root}`]: { maxWidth: 658 } }}
              >
                <ImportDetails onNextPanel={() => setExpanded("panel2")} />
              </AccordionDetails>
            </Accordion>

            {/* 2. Table Configuration */}
            <Accordion
              expanded={expanded === "panel2"}
              disabled={
                yn(VITE_DATASET_INGESTION_JOBS_ENABLED)
                  ? !(storageKey && destination)
                  : !(storageKey && destination && tableName) ||
                    form.getFieldState("metadata_config.table").invalid
              }
              onChange={handleExpand("panel2")}
            >
              <AccordionSummary>
                <T variant="body2" fontWeight={500}>
                  2. Table Configuration
                </T>
              </AccordionSummary>
              <AccordionDetails>
                <TableMetadataDetails onNextPanel={() => setExpanded("panel3")} />
              </AccordionDetails>
            </Accordion>

            {/* 3. Review */}
            <Accordion
              expanded={expanded === "panel3"}
              disabled={!isFormValidForIngestion}
              onChange={handleExpand("panel3")}
            >
              <AccordionSummary>
                <T variant="body2" fontWeight={500}>
                  3. Review
                </T>
              </AccordionSummary>
              <AccordionDetails>
                <ReviewDetails />
              </AccordionDetails>
            </Accordion>
          </Stack>
        </form>
      </FormProvider>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Replace existing table?</DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            <T variant="body2">
              The table{" "}
              <T component="span" variant="body2" fontWeight={600}>
                {`${form.getValues("metadata_config.database_schema")}.${form.getValues(
                  "metadata_config.table"
                )}`}
              </T>{" "}
              already exists. Proceeding will permanently replace all of its current data with the
              contents of this ingestion.
            </T>
            <T variant="body2" sx={{ opacity: 0.7 }}>
              The previous data will no longer be accessible once the ingestion completes.
            </T>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmOpen(false);
              trackEvent("dataset_table_overwrite_confirmed", {
                dataset_id: datasetId,
                table_name: form.getValues("metadata_config.table"),
                database_schema: form.getValues("metadata_config.database_schema"),
              });
              form.handleSubmit(doSubmit)();
            }}
          >
            Replace Table
          </Button>
        </DialogActions>
      </Dialog>
      {import.meta.env.DEV && (
        <Portal>
          <DevTool control={form.control} placement="bottom-right" />
        </Portal>
      )}
    </>
  );
};

export default IngestForm;
